// eslint-disable-next-line no-restricted-imports
import { message } from 'antd';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { AutomationType } from 'api/automations';
import { CustomAutomationsApi } from 'api/automations/custom-automation-api';
import { PlanningLevelConfig } from 'api/resource-management/types';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { setDirtyState } from 'data/templates';
import { TemplateObjectType, TemplatesAPI } from 'data/templates/api';
import { deleteTemplateFromIndexedDb, getTemplateDraftFromIndexedDb } from 'hooks/use-idb';
import { getResourceManagementConfigQueryKey } from 'queries/resource-management-keys';
import { ROLES_QUERY_KEY, ROLES_WITH_PLACEHOLDER_QUERY_KEY } from 'queries/roles';
import { logger } from 'services';
import { migrations } from 'workers/migrations';
import { fetchProjectTemplateStart, fetchProjectTemplateStartSuccess } from '..';
import { fetchProjectTemplate } from '../actions';
import { getProjectTemplatePhaseIds } from '../selectors';
import { setDynamicConditions, setProjectTemplateActiveRecord } from '../slices';
import { generateNewTaskRows, hoursPerWeekModifierRedux } from './utils';
const isTemplateProjectTemplate = (data)=>(data === null || data === void 0 ? void 0 : data.objectType) === TemplateObjectType.Project;
function* fetchProjectTemplateSaga(action) {
    const queryClient = yield getQueryClientContext();
    try {
        var _data_templateContent_projectPhases, _data_templateContent;
        const { payload } = action;
        const { templateId, dynamicConditionFields } = payload;
        // const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
        //   getQueryAdvancedSettingsConfigKey(),
        // );
        // const newDependenciesSettingEnabled =
        //   getNewDependenciesEnabledSetting(accountSettings);
        yield put(fetchProjectTemplateStart({
            templateId
        }));
        const automations = yield CustomAutomationsApi.getAllAutomationsForParams({
            automationType: AutomationType.CustomAutomation,
            projectTemplateId: templateId
        });
        const templateData = yield TemplatesAPI.getById(templateId);
        const templateDataWithAutomations = {
            ...templateData,
            automations
        };
        let data = migrations.addEntityOrderToProjectTemplatePhases(templateDataWithAutomations);
        // if (!newDependenciesSettingEnabled) {
        //   data = migrations.removeNewDependenciesFromProjectTemplate(data);
        // }
        const templateFromDbPromise = getTemplateDraftFromIndexedDb(templateId);
        const automationIdsFromBE = automations.map((e)=>e.automationId);
        const dataFromIdbWithoutPreProcessing = yield templateFromDbPromise;
        let content = dataFromIdbWithoutPreProcessing === null || dataFromIdbWithoutPreProcessing === void 0 ? void 0 : dataFromIdbWithoutPreProcessing.content;
        if (content !== undefined && isTemplateProjectTemplate(content)) {
            content = {
                ...content,
                automations: (content === null || content === void 0 ? void 0 : content.automations) ? content === null || content === void 0 ? void 0 : content.automations.filter((e)=>e.automationId < 0 ? true : automationIdsFromBE.includes(e.automationId)) : []
            };
        }
        const dataFromIdb = dataFromIdbWithoutPreProcessing === null || dataFromIdbWithoutPreProcessing === undefined ? null : {
            ...dataFromIdbWithoutPreProcessing,
            content
        };
        if (dataFromIdb != null && dataFromIdb.content.objectType === TemplateObjectType.Project) {
            var _ref, _data_publishedAt;
            if (dataFromIdb.timestamp > ((_ref = (_data_publishedAt = data.publishedAt) !== null && _data_publishedAt !== void 0 ? _data_publishedAt : data.createdAt) !== null && _ref !== void 0 ? _ref : 0)) {
                data = dataFromIdb.content;
                yield put(setDirtyState({
                    id: templateId,
                    _state: true
                }));
            } else {
                const arrayOfDocIds = [];
                dataFromIdb.content.templateContent.spaceList.forEach((x)=>{
                    x.spaceTabs.forEach((y)=>{
                        arrayOfDocIds.push(y.spaceTabId);
                    });
                });
                arrayOfDocIds.push(templateId);
                yield deleteTemplateFromIndexedDb(arrayOfDocIds);
            }
        }
        const rmConfig = queryClient.getQueryData(getResourceManagementConfigQueryKey());
        // phaseId was being passed, which is not correct, so we are sanitizing it for now
        // this should be removed after the migration
        data = {
            ...data,
            templateContent: {
                ...data.templateContent,
                projectPhases: data === null || data === void 0 ? void 0 : (_data_templateContent = data.templateContent) === null || _data_templateContent === void 0 ? void 0 : (_data_templateContent_projectPhases = _data_templateContent.projectPhases) === null || _data_templateContent_projectPhases === void 0 ? void 0 : _data_templateContent_projectPhases.map((phase)=>{
                    var _ref;
                    var _phase_dependentProjectPhases;
                    return {
                        ...phase,
                        dependentProjectPhases: (_ref = phase === null || phase === void 0 ? void 0 : (_phase_dependentProjectPhases = phase.dependentProjectPhases) === null || _phase_dependentProjectPhases === void 0 ? void 0 : _phase_dependentProjectPhases.filter((dep)=>{
                            // check if phaseId is present instead of projectPhaseId, if yes, remove it
                            if (dep === null || dep === void 0 ? void 0 : dep.phaseId) {
                                return false;
                            }
                            return true;
                        })) !== null && _ref !== void 0 ? _ref : []
                    };
                })
            }
        };
        const newData = hoursPerWeekModifierRedux(data, (rmConfig === null || rmConfig === void 0 ? void 0 : rmConfig.planningLevelConfig) === PlanningLevelConfig.HoursPerWeek);
        const dataWithNewTaskRows = generateNewTaskRows(newData);
        const rolesWithPlaceholder = queryClient.getQueryData(ROLES_WITH_PLACEHOLDER_QUERY_KEY);
        const roles = queryClient.getQueryData(ROLES_QUERY_KEY);
        const { byId: teamsById } = yield select(({ teams })=>teams);
        yield put(fetchProjectTemplateStartSuccess({
            template: dataWithNewTaskRows,
            rolesWithPlaceholder,
            allRoles: roles,
            teamsLookup: teamsById
        }));
        const phaseIds = yield select(getProjectTemplatePhaseIds(templateId));
        if (phaseIds[0]) {
            yield put(setProjectTemplateActiveRecord({
                id: phaseIds[0],
                type: 'PROJECT_PHASE'
            }));
        }
        const { getDynamicConditionsFromPayload } = yield call(()=>import('../api/transform-filters'));
        const dynamicConditions = getDynamicConditionsFromPayload(data.templateContent, dynamicConditionFields);
        yield put(setDynamicConditions(dynamicConditions));
    } catch (error) {
        logger.error('an error fetching the project template', {
            error
        });
        message.error('There was an error fetching the project template');
    }
}
export function* watchFetchProjectTemplateSaga() {
    yield takeLatest(fetchProjectTemplate.type, fetchProjectTemplateSaga);
}
